import { useState, useRef, useEffect, type ReactNode } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Send, Bot } from 'lucide-react'
import { useTelegram } from '../hooks/useTelegram'
import { setPavelUnread } from '../hooks/usePavelUnread'
import { api } from '../services/api'
import { MessageRole } from '@tfa/shared'

const F = 'Roboto, sans-serif'

interface Message {
  id: string
  role: `${MessageRole}`
  content: string
  createdAt: string
}

function toUiRole(role: 'user' | 'assistant'): `${MessageRole}` {
  return role === 'user' ? 'USER' : 'ASSISTANT'
}

// Inline markdown — supports **bold**, *italic*, `code`.
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = []
  const regex = /(\*\*([^*]+)\*\*|`([^`]+)`|(?<!\*)\*([^*\n]+)\*(?!\*))/g
  let lastIndex = 0
  let match: RegExpExecArray | null
  let i = 0
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index))
    if (match[2] !== undefined) nodes.push(<strong key={`${keyPrefix}-b-${i}`}>{match[2]}</strong>)
    else if (match[3] !== undefined) nodes.push(<code key={`${keyPrefix}-c-${i}`} style={{ background: 'rgba(26,58,92,0.08)', padding: '1px 5px', borderRadius: 4, fontSize: 13 }}>{match[3]}</code>)
    else if (match[4] !== undefined) nodes.push(<em key={`${keyPrefix}-i-${i}`}>{match[4]}</em>)
    lastIndex = match.index + match[0].length
    i++
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex))
  return nodes
}

// Block-level markdown — headings (### / ##), bullet lists (- / •),
// numbered lists (1.), horizontal rule (---), and plain paragraphs.
function Markdown({ content }: { content: string }) {
  const lines = content.split('\n')
  const blocks: ReactNode[] = []
  let list: { type: 'ul' | 'ol'; items: string[]; startIdx: number } | null = null

  const flushList = () => {
    if (!list) return
    const items = list.items.map((it, idx) => (
      <li key={`li-${list!.startIdx}-${idx}`} style={{ marginBottom: 2 }}>
        {renderInline(it, `li-${list!.startIdx}-${idx}`)}
      </li>
    ))
    const style = { margin: '4px 0 4px 0', paddingLeft: 20 } as const
    blocks.push(list.type === 'ul'
      ? <ul key={`ul-${list.startIdx}`} style={style}>{items}</ul>
      : <ol key={`ol-${list.startIdx}`} style={style}>{items}</ol>
    )
    list = null
  }

  lines.forEach((raw, idx) => {
    const trimmed = raw.trim()
    if (trimmed === '---' || trimmed === '___') {
      flushList()
      blocks.push(<hr key={`hr-${idx}`} style={{ border: 'none', borderTop: '1px solid rgba(26,58,92,0.15)', margin: '8px 0' }} />)
      return
    }
    const heading = /^(#{1,3})\s+(.*)$/.exec(trimmed)
    if (heading) {
      flushList()
      const level = heading[1].length
      const text = heading[2]
      const size = level === 1 ? 18 : level === 2 ? 16 : 15
      blocks.push(
        <div key={`h-${idx}`} style={{ fontSize: size, fontWeight: 600, margin: '6px 0 2px' }}>
          {renderInline(text, `h-${idx}`)}
        </div>
      )
      return
    }
    const bullet = /^\s*[-•*]\s+(.*)$/.exec(raw)
    if (bullet) {
      if (!list || list.type !== 'ul') { flushList(); list = { type: 'ul', items: [], startIdx: idx } }
      list.items.push(bullet[1])
      return
    }
    const numbered = /^\s*\d+\.\s+(.*)$/.exec(raw)
    if (numbered) {
      if (!list || list.type !== 'ol') { flushList(); list = { type: 'ol', items: [], startIdx: idx } }
      list.items.push(numbered[1])
      return
    }
    flushList()
    if (trimmed === '') {
      blocks.push(<div key={`sp-${idx}`} style={{ height: 6 }} />)
      return
    }
    blocks.push(
      <div key={`p-${idx}`} style={{ whiteSpace: 'pre-wrap' }}>
        {renderInline(raw, `p-${idx}`)}
      </div>
    )
  })
  flushList()
  return <>{blocks}</>
}

interface Props {
  /** When true, renders without its own full-page scroll container — for use inside a bottom sheet. */
  embedded?: boolean
  /** Optional canned assistant message rendered at the top of the active conversation.
   * Used by the SOS flow on Diary to frame the topic before the user types. Not persisted to DB. */
  seedAssistantMessage?: string
  /** Suggested user-side quick-reply chips shown above the input.
   * Click → pre-fills the input but doesn't auto-send. */
  suggestedReplies?: string[]
}

export function PavelChatBody({ embedded = false, seedAssistantMessage, suggestedReplies }: Props) {
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [errorBanner, setErrorBanner] = useState<string | null>(null)
  // True while we faux-type the seed assistant message so the user sees Pavel
  // "writing" before the canned greeting appears. Drives the typing indicator.
  const [seedTyping, setSeedTyping] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const { impactHaptic } = useTelegram()

  // На открытии чата сбрасываем "непрочитано" — пользователь сейчас в диалоге.
  // Когда чат закрывается, флаг не трогаем: новые сообщения от Pavel приходят
  // только в открытом чате, и dot на иконке робот отражает «не дочитал».
  useEffect(() => {
    setPavelUnread(false)
  }, [])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => { scrollToBottom() }, [messages])

  const historyQuery = useQuery({
    queryKey: ['ai-chat-history'],
    queryFn: () => api.getChatHistory(),
    staleTime: 60_000,
  })

  // Seed messages from history once it loads. We don't overwrite on refetch —
  // the user may have sent messages optimistically that aren't in history yet.
  // When `seedAssistantMessage` is provided (SOS flow), append it AFTER history
  // so the canned framing sits at the bottom as the current topic prompt.
  const historyLoaded = useRef(false)
  useEffect(() => {
    if (historyLoaded.current || !historyQuery.data) return
    historyLoaded.current = true
    const seeded: Message[] = historyQuery.data.messages.map((m) => ({
      id: m.id,
      role: toUiRole(m.role),
      content: m.content,
      createdAt: m.createdAt,
    }))
    setMessages(seeded)
    if (seedAssistantMessage) {
      // Эмулируем «Pavel пишет…» 2.5с прежде чем показать seed-приветствие,
      // чтобы пользователь видел зарождение сообщения, а не сразу его конец.
      setSeedTyping(true)
      const timer = setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          {
            id: `seed-${Date.now()}`,
            role: 'ASSISTANT',
            content: seedAssistantMessage,
            createdAt: new Date().toISOString(),
          },
        ])
        setSeedTyping(false)
        // Помечаем "непрочитанным" — если пользователь свернёт чат, на иконке
        // бота загорится точка-напоминалка вернуться.
        setPavelUnread(true)
      }, 2500)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [historyQuery.data, seedAssistantMessage])

  const sendMessageMutation = useMutation({
    mutationFn: (message: string) => api.sendMessage(message),
    onSuccess: (data) => {
      setErrorBanner(null)
      setMessages((prev) => [...prev, {
        id: data.assistantMessage.id,
        role: 'ASSISTANT',
        content: data.assistantMessage.content,
        createdAt: data.assistantMessage.createdAt,
      }])
      // Новое сообщение Pavel → если пользователь свернёт sheet, на иконке
      // бота должна загораться точка-напоминалка.
      setPavelUnread(true)
    },
    onError: (err: any) => {
      const status = err?.response?.status
      if (status === 503) {
        // #365: «попробуйте через минуту» вводило в заблуждение — при
        // исчерпанном балансе Anthropic Павел молчит до пополнения, а не минуту.
        setErrorBanner('Павел вернётся позже')
      } else {
        setErrorBanner('Не удалось отправить сообщение. Попробуйте ещё раз.')
      }
    },
  })

  const handleSend = () => {
    if (!input.trim() || sendMessageMutation.isPending) return
    impactHaptic('light')
    const text = input.trim()
    setMessages((prev) => [...prev, {
      id: `local-${Date.now()}`,
      role: 'USER',
      content: text,
      createdAt: new Date().toISOString(),
    }])
    sendMessageMutation.mutate(text)
    setInput('')
  }

  const suggestedQuestions = [
    'Как правильно считать калории?',
    'Какие упражнения для похудения?',
    'Как поддерживать мотивацию?',
    'Вес стоит на месте — что делать?',
  ]

  // Quick-reply chips for current topic (e.g. SOS) — sit above input.
  const quickReplies = suggestedReplies ?? []

  const rootStyle = embedded
    ? { fontFamily: F, display: 'flex', flexDirection: 'column' as const, flex: 1, minHeight: 0 }
    : { fontFamily: F, display: 'flex', flexDirection: 'column' as const, minHeight: 'calc(100vh - 20px)' }

  return (
    <div style={rootStyle}>
      {/* Messages area */}
      <div style={{ flex: 1, padding: '12px 16px', overflowY: 'auto' }}>
        {messages.length === 0 && !seedTyping ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: embedded ? 20 : 48, gap: 12 }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg,#6C5CE7,#A29BFE)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Bot size={32} color="white" />
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 600, color: '#1C2D41', margin: 0 }}>Привет! Я AI Павел</h2>
            <p style={{ fontSize: 15, color: '#6B7B8D', textAlign: 'center', margin: 0, lineHeight: 1.5 }}>
              Твой персональный помощник по фитнесу и питанию
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', marginTop: 16 }}>
              <span style={{ fontSize: 13, color: '#9AA5B4', textAlign: 'center' }}>Популярные вопросы:</span>
              {suggestedQuestions.map((q, i) => (
                <button
                  key={i}
                  onClick={() => setInput(q)}
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: 12,
                    border: 'none', background: 'rgba(255,255,255,0.75)',
                    backdropFilter: 'blur(20px)', fontSize: 14, color: '#4A9FE5',
                    cursor: 'pointer', textAlign: 'left', fontWeight: 500,
                  }}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {messages.map((msg) => (
              <div key={msg.id} style={{ display: 'flex', justifyContent: msg.role === 'USER' ? 'flex-end' : 'flex-start', gap: 8 }}>
                {msg.role === 'ASSISTANT' && (
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#6C5CE7,#A29BFE)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                    <Bot size={14} color="white" />
                  </div>
                )}
                <div style={{
                  maxWidth: '75%', padding: '10px 14px', borderRadius: 16,
                  background: msg.role === 'USER'
                    ? 'linear-gradient(135deg, #1A3A5C, #4A6CF7)'
                    : 'rgba(255,255,255,0.85)',
                  backdropFilter: msg.role === 'ASSISTANT' ? 'blur(20px)' : undefined,
                  color: msg.role === 'USER' ? 'white' : '#1C2D41',
                  fontSize: 15, lineHeight: 1.5,
                  borderBottomRightRadius: msg.role === 'USER' ? 4 : 16,
                  borderBottomLeftRadius: msg.role === 'ASSISTANT' ? 4 : 16,
                  whiteSpace: msg.role === 'USER' ? 'pre-wrap' : undefined,
                  wordBreak: 'break-word',
                }}>
                  {msg.role === 'ASSISTANT'
                    ? <Markdown content={msg.content} />
                    : msg.content}
                </div>
              </div>
            ))}
            {(sendMessageMutation.isPending || seedTyping) && (
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#6C5CE7,#A29BFE)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Bot size={14} color="white" />
                </div>
                <div style={{ padding: '12px 16px', borderRadius: 16, background: 'rgba(255,255,255,0.85)', borderBottomLeftRadius: 4, display: 'flex', gap: 4 }}>
                  {[0, 1, 2].map(i => (
                    <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: '#A0AEC0', animation: `typing 1.2s ease-in-out ${i * 0.2}s infinite` }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {errorBanner && (
        <div style={{
          margin: '0 16px 8px', padding: '8px 12px', borderRadius: 12,
          background: 'rgba(255, 80, 80, 0.12)', color: '#B43A3A', fontSize: 13,
        }}>
          {errorBanner}
        </div>
      )}

      {/* Topic-specific quick replies (above input) */}
      {quickReplies.length > 0 && (
        <div style={{
          display: 'flex', gap: 6,
          padding: '6px 16px 0',
          overflowX: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}>
          {quickReplies.map((q, i) => (
            <button
              key={i}
              onClick={() => setInput(q)}
              style={{
                flexShrink: 0,
                height: 32, padding: '0 12px', borderRadius: 16,
                background: 'rgba(74,108,247,0.10)',
                border: '1px solid rgba(74,108,247,0.25)',
                color: '#4A6CF7', fontSize: 12, fontWeight: 600,
                cursor: 'pointer', whiteSpace: 'nowrap',
                fontFamily: F,
              }}
            >
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div style={{
        padding: '8px 16px',
        paddingBottom: embedded ? 8 : 'calc(8px + env(safe-area-inset-bottom, 0px))',
        display: 'flex', gap: 8, alignItems: 'flex-end',
        background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(20px)',
        borderTop: '1px solid rgba(255,255,255,0.4)',
      }}>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() } }}
          placeholder="Напиши сообщение..."
          rows={1}
          disabled={sendMessageMutation.isPending}
          style={{
            flex: 1, padding: '10px 14px', borderRadius: 16,
            border: '1px solid rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.6)',
            fontSize: 15, color: '#1C2D41', fontFamily: F,
            outline: 'none', resize: 'none', minHeight: 42, maxHeight: 120,
          }}
        />
        <button
          onClick={handleSend}
          disabled={!input.trim() || sendMessageMutation.isPending}
          style={{
            width: 42, height: 42, borderRadius: '50%', border: 'none', flexShrink: 0,
            background: !input.trim() ? 'rgba(26,58,92,0.06)' : 'linear-gradient(135deg, #1A3A5C, #4A6CF7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: !input.trim() ? 'default' : 'pointer',
          }}
        >
          <Send size={18} color={!input.trim() ? '#A0AEC0' : 'white'} />
        </button>
      </div>

      <style>{`
        @keyframes typing {
          0%, 100% { opacity: 0.3; transform: translateY(0); }
          50% { opacity: 1; transform: translateY(-3px); }
        }
      `}</style>
    </div>
  )
}
